import { useMemo, useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { Select } from '../../../shared/components/Select';
import { DropdownProvider } from '../../../shared/utils/DropdownContext';
import { useMerchants } from '../../merchants/hooks/useMerchants';
import { useCreateEmailCampaign } from '../hooks/useEmailCampaigns';
import { canSubmitCampaign, parseRecipients } from '../recipients';

interface CreateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Compose + send a news/offer campaign (PE-2125).
 *
 * Two things drive this form's shape, both from the BFF contract:
 *
 *  1. **It sends immediately.** `POST /email-campaigns` is documented "create and
 *     immediately send" — no draft, no schedule, no recall. So the primary action
 *     is a two-step confirm, not a bare Send.
 *  2. **`solutionId` is not the user's problem.** The DTO demands it, but every
 *     merchant already carries one, so it's derived from the chosen merchant
 *     rather than being a second picker the user could mismatch.
 *
 * `templateId` is a free-text field on purpose: PE-891 exposes no template
 * catalogue, so there is nothing to populate a dropdown from. Tracked in PE-2204.
 */
export function CreateCampaignModal({ isOpen, onClose }: CreateCampaignModalProps) {
  const [title, setTitle] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [recipientsRaw, setRecipientsRaw] = useState('');
  const [confirming, setConfirming] = useState(false);

  const { data: merchants = [], isLoading: merchantsLoading } = useMerchants({ enabled: isOpen });
  const createCampaign = useCreateEmailCampaign();

  const recipients = useMemo(() => parseRecipients(recipientsRaw), [recipientsRaw]);
  const selectedMerchant = merchants.find((m) => m.id === tenantId);
  const canSubmit = canSubmitCampaign({ title, tenantId, templateId, recipients });

  const merchantOptions = useMemo(
    () => merchants.map((m) => ({ title: m.name, value: m.id })),
    [merchants],
  );

  const reset = () => {
    setTitle('');
    setTenantId('');
    setTemplateId('');
    setRecipientsRaw('');
    setConfirming(false);
  };

  const close = () => {
    if (createCampaign.isPending) return;
    reset();
    onClose();
  };

  const send = async () => {
    if (!canSubmit || !selectedMerchant) return;
    try {
      await createCampaign.mutateAsync({
        title: title.trim(),
        tenantId,
        solutionId: selectedMerchant.solutionId,
        templateId: templateId.trim(),
        recipientEmails: recipients.valid,
      });
      reset();
      onClose();
    } catch {
      // Already surfaced by the mutation's onError; keep the form open with the
      // user's input intact so they can retry rather than retype.
      setConfirming(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size="lg">
      <DropdownProvider>
        <div className="p-6">
          <h2 className="text-[20px] font-bold text-foreground">New campaign</h2>
          <p className="text-[13px] text-muted-foreground mt-1">
            Sends as soon as you confirm — there is no draft or scheduled state.
          </p>

          <div className="mt-6 space-y-5">
            <Input
              label="Campaign title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="August news & offers"
            />

            <Select
              label="Merchant"
              options={merchantOptions}
              value={tenantId}
              onChange={setTenantId}
              placeholder={merchantsLoading ? 'Loading merchants…' : 'Select a merchant'}
              disabled={merchantsLoading}
            />
            {selectedMerchant && (
              <p className="-mt-3 text-[12px] text-muted-foreground">
                Solution: <span className="text-foreground">{selectedMerchant.solutionId}</span>
              </p>
            )}

            <Input
              label="Template ID"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              placeholder="news-and-offers-august"
            />
            <p className="-mt-3 text-[12px] text-muted-foreground">
              Free text — the backend has no template catalogue to pick from yet.
            </p>

            <div>
              <label
                htmlFor="campaign-recipients"
                className="block text-[14px] font-[600] text-foreground mb-1"
              >
                Recipients
              </label>
              <textarea
                id="campaign-recipients"
                value={recipientsRaw}
                onChange={(e) => setRecipientsRaw(e.target.value)}
                rows={5}
                placeholder="ada@example.com, chidi@example.com"
                className="w-full rounded-[8px] border border-border bg-background px-3 py-2 text-sm text-foreground"
              />
              <div className="mt-1 flex flex-wrap gap-x-4 text-[12px]">
                <span className="text-muted-foreground">
                  Separate with commas, semicolons or new lines.
                </span>
                {recipients.valid.length > 0 && (
                  <span className="text-foreground">
                    {recipients.valid.length} recipient
                    {recipients.valid.length === 1 ? '' : 's'}
                  </span>
                )}
                {recipients.duplicateCount > 0 && (
                  <span className="text-muted-foreground">
                    {recipients.duplicateCount} duplicate
                    {recipients.duplicateCount === 1 ? '' : 's'} removed
                  </span>
                )}
              </div>
              {recipients.invalid.length > 0 && (
                <p className="mt-2 text-[12px] text-red-500">
                  Not valid email addresses: {recipients.invalid.join(', ')}
                </p>
              )}
            </div>
          </div>

          {/* ---- Actions ---- */}
          <div className="mt-8 flex items-center justify-end gap-3">
            <Button variant="outline" onClick={close} disabled={createCampaign.isPending}>
              Cancel
            </Button>

            {confirming ? (
              <>
                <span className="text-[13px] text-muted-foreground mr-auto">
                  Send to {recipients.valid.length} recipient
                  {recipients.valid.length === 1 ? '' : 's'} now? This cannot be undone.
                </span>
                <Button variant="outline" onClick={() => setConfirming(false)} disabled={createCampaign.isPending}>
                  Back
                </Button>
                <Button onClick={send} loading={createCampaign.isPending}>
                  Confirm & send
                </Button>
              </>
            ) : (
              <Button onClick={() => setConfirming(true)} disabled={!canSubmit}>
                Send campaign
              </Button>
            )}
          </div>
        </div>
      </DropdownProvider>
    </Modal>
  );
}
